import { X, Receipt, MapPin, Calendar, CreditCard, FileText, Ticket } from 'lucide-react'

const ExperienceCommissionBookingsModal = ({
  isOpen,
  onClose,
  commission,
  formatCurrency,
  formatDate,
  formatMonth
}) => {
  if (!isOpen || !commission) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Experience Bookings - {formatMonth(commission.commissionMonth)}
            </h3>
            <p className="text-slate-600 text-sm mt-1">
              Total Booking Value: {formatCurrency(commission.totalBookingValue)} •
              {commission.bookings?.length || 0} Booking(s)
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="p-6">
            {commission.bookings && commission.bookings.length > 0 ? (
              <div className="space-y-4">
                {commission.bookings.map((booking, index) => (
                  <div key={booking._id || index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                          <Ticket className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900 text-lg">
                            Booking #{index + 1}
                          </p>
                          <p className="text-sm text-slate-500">
                            Confirmation: {booking.confirmationId}
                          </p>
                          {booking.experienceName && (
                            <p className="text-sm text-slate-600 mt-1">
                              Experience: {booking.experienceName}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-purple-600">
                          {formatCurrency(booking.bookingValue)}
                        </p>
                        <p className="text-sm text-slate-500">
                          {formatDate(booking.bookingDate)}
                        </p>
                      </div>
                    </div>

                    {/* Booking Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-slate-600 font-medium mb-1 flex items-center">
                          <CreditCard className="h-4 w-4 mr-2" />
                          Booking Value
                        </p>
                        <p className="text-slate-900 font-semibold">
                          {formatCurrency(booking.bookingValue)}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-slate-600 font-medium mb-1 flex items-center">
                          <CreditCard className="h-4 w-4 mr-2" />
                          Base Amount
                        </p>
                        <p className="text-slate-900 font-semibold">
                          {formatCurrency(booking.baseAmount)}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-slate-600 font-medium mb-1 flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          Booking Date
                        </p>
                        <p className="text-slate-900">
                          {formatDate(booking.bookingDate)}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-slate-600 font-medium mb-1 flex items-center">
                          <Ticket className="h-4 w-4 mr-2" />
                          Experience Name
                        </p>
                        <p className="text-slate-900">
                          {booking.experienceName || 'Not specified'}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-slate-600 font-medium mb-1 flex items-center">
                          <FileText className="h-4 w-4 mr-2" />
                          Confirmation ID
                        </p>
                        <p className="text-slate-900 font-mono text-sm">
                          {booking.confirmationId}
                        </p>
                      </div>

                      {booking.experienceId && (
                        <div className="bg-white rounded-lg p-3 border border-slate-200">
                          <p className="text-slate-600 font-medium mb-1 flex items-center">
                            <MapPin className="h-4 w-4 mr-2" />
                            Experience ID
                          </p>
                          <p className="text-slate-900 font-mono text-sm">
                            {booking.experienceId}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Markup Display */}
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Commission Amount:</span>
                        <div className="text-right">
                          <span className="text-green-600 font-bold">
                            {formatCurrency(booking.bookingValue - booking.baseAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Summary Card */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-purple-600 font-medium mb-1">Total Bookings</p>
                      <p className="text-2xl font-bold text-purple-900">{commission.bookings.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium mb-1">Total Booking Value</p>
                      <p className="text-2xl font-bold text-purple-900">
                        {formatCurrency(commission.totalBookingValue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium mb-1">Commission Earned</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(commission.totalCommissionForMonth)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No booking details available</p>
                <p className="text-slate-400 text-sm mt-2">
                  Booking information for this commission period is not available
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              {commission.bookings?.length || 0} booking(s) found
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExperienceCommissionBookingsModal
